/**
 * PR-36 — Global Team Identity Tokens.
 *
 * Centralized emotional identity per national team. Extends PR-35D
 * (TEAM_PALETTES, color base) with the missing emotional dimensions:
 * glow intensity, ambient aura, shadow language, overlay accents,
 * tactical highlight, heat / danger / pulse colors and contrast layer.
 *
 * NO inline hardcoding: every component that wants identity reads from
 * the CSS variables emitted by `applyIdentityTokens` (see
 * `liveThemeApplication.applyLiveTheme`). All values are computed in
 * `color-mix` so they degrade gracefully when the user has not picked
 * a team yet (DEFAULT profile).
 */
import type { TeamId, TeamPalette } from "@/lib/pr35d/teamThemeEngine";
import { getPaletteById } from "@/lib/pr35d/teamThemeEngine";

/** Emotional family — drives shadow + animation register. */
export type EmotionalFamily =
  | "tropical"   // warm, vibrating, jungle-stadium
  | "icy"        // cool, tactical, platinum
  | "vibrant"    // saturated, mexican-wave energy
  | "royal"      // electric, prestige
  | "tactical"   // sharp, contrast, disciplined
  | "warm"       // ember, mediterranean
  | "minimal"    // elegant, monochrome
  | "aggressive" // crimson pulse
  | "elegant"    // refined italianate
  | "sunny"      // tropical sunlit
  | "electric"   // stars-and-stripes pop
  | "calm"       // celeste serene
  | "fierce"     // andean fire
  | "bright"     // yellow energy
  | "neon";      // default VerseSport

/** Shadow language — how the team "presses" on the UI. */
export type ShadowLanguage = "sharp" | "diffuse" | "soft" | "halo";

export interface TeamIdentityProfile {
  id: TeamId;
  family: EmotionalFamily;
  /** 0 (subtle) .. 1 (cinematic) — drives glow alpha + spread. */
  intensity: number;
  shadowLanguage: ShadowLanguage;
  /** Color used for ambient radial behind hero zones. */
  auraColor: string;
  /** Color used for stadium pressure / heat overlays. */
  heatColor: string;
  /** Color used for danger / goal-imminent flashes. */
  dangerColor: string;
  /** Color used for tactical highlights (pitch HUD, momentum). */
  tacticalHighlight: string;
  /** Overlay accent for HUD chips, badges, predictive vectors. */
  overlayAccent: string;
  /** Pulse color used by pulse-team / live dot. */
  pulseColor: string;
  /** Foreground color guaranteed to read on top of primary. */
  contrast: string;
}

/** Helper — derive a profile when no curated entry exists. */
function fallbackProfile(id: TeamId): TeamIdentityProfile {
  const p = getPaletteById(id);
  return {
    id,
    family: "neon",
    intensity: 0.6,
    shadowLanguage: "diffuse",
    auraColor: p.primary,
    heatColor: p.tertiary,
    dangerColor: p.tertiary,
    tacticalHighlight: p.secondary,
    overlayAccent: p.primary,
    pulseColor: p.primary,
    contrast: "#ffffff",
  };
}

/** Curated emotional profiles — all 15 World Cup palettes + DEFAULT. */
const PROFILES: Record<TeamId, TeamIdentityProfile> = {
  DEFAULT: {
    id: "DEFAULT", family: "neon", intensity: 0.55, shadowLanguage: "diffuse",
    auraColor: "oklch(0.82 0.24 145)", heatColor: "oklch(0.78 0.18 60)",
    dangerColor: "oklch(0.70 0.22 25)", tacticalHighlight: "oklch(0.65 0.22 265)",
    overlayAccent: "oklch(0.82 0.24 145)", pulseColor: "oklch(0.82 0.24 145)",
    contrast: "#0a0a0a",
  },
  BRA: {
    id: "BRA", family: "tropical", intensity: 0.95, shadowLanguage: "halo",
    auraColor: "#FFDF00", heatColor: "#FF7A00", dangerColor: "#E0102E",
    tacticalHighlight: "#009C3B", overlayAccent: "#FFDF00", pulseColor: "#009C3B",
    contrast: "#0a1a0a",
  },
  ARG: {
    id: "ARG", family: "icy", intensity: 0.85, shadowLanguage: "sharp",
    auraColor: "#75AADB", heatColor: "#F6B40E", dangerColor: "#0d6efd",
    tacticalHighlight: "#75AADB", overlayAccent: "#F6B40E", pulseColor: "#75AADB",
    contrast: "#0a1a30",
  },
  MEX: {
    id: "MEX", family: "vibrant", intensity: 0.90, shadowLanguage: "halo",
    auraColor: "#006847", heatColor: "#CE1126", dangerColor: "#CE1126",
    tacticalHighlight: "#FFFFFF", overlayAccent: "#006847", pulseColor: "#CE1126",
    contrast: "#ffffff",
  },
  FRA: {
    id: "FRA", family: "royal", intensity: 0.80, shadowLanguage: "sharp",
    auraColor: "#0055A4", heatColor: "#EF4135", dangerColor: "#EF4135",
    tacticalHighlight: "#FFFFFF", overlayAccent: "#0055A4", pulseColor: "#EF4135",
    contrast: "#ffffff",
  },
  GER: {
    id: "GER", family: "tactical", intensity: 0.75, shadowLanguage: "sharp",
    auraColor: "#FFCE00", heatColor: "#DD0000", dangerColor: "#DD0000",
    tacticalHighlight: "#FFCE00", overlayAccent: "#FFFFFF", pulseColor: "#FFCE00",
    contrast: "#ffffff",
  },
  ESP: {
    id: "ESP", family: "warm", intensity: 0.85, shadowLanguage: "diffuse",
    auraColor: "#F1BF00", heatColor: "#AA151B", dangerColor: "#AA151B",
    tacticalHighlight: "#F1BF00", overlayAccent: "#AA151B", pulseColor: "#F1BF00",
    contrast: "#1a0a0a",
  },
  ENG: {
    id: "ENG", family: "minimal", intensity: 0.55, shadowLanguage: "soft",
    auraColor: "#FFFFFF", heatColor: "#CE1124", dangerColor: "#CE1124",
    tacticalHighlight: "#012169", overlayAccent: "#012169", pulseColor: "#CE1124",
    contrast: "#0a0a1a",
  },
  POR: {
    id: "POR", family: "aggressive", intensity: 0.92, shadowLanguage: "halo",
    auraColor: "#DA291C", heatColor: "#DA291C", dangerColor: "#DA291C",
    tacticalHighlight: "#FFD200", overlayAccent: "#046A38", pulseColor: "#DA291C",
    contrast: "#ffffff",
  },
  ITA: {
    id: "ITA", family: "elegant", intensity: 0.78, shadowLanguage: "soft",
    auraColor: "#008C45", heatColor: "#CD212A", dangerColor: "#CD212A",
    tacticalHighlight: "#008C45", overlayAccent: "#F4F5F0", pulseColor: "#008C45",
    contrast: "#ffffff",
  },
  COL: {
    id: "COL", family: "sunny", intensity: 0.88, shadowLanguage: "halo",
    auraColor: "#FCD116", heatColor: "#CE1126", dangerColor: "#CE1126",
    tacticalHighlight: "#003893", overlayAccent: "#FCD116", pulseColor: "#CE1126",
    contrast: "#1a1500",
  },
  USA: {
    id: "USA", family: "electric", intensity: 0.82, shadowLanguage: "sharp",
    auraColor: "#3C3B6E", heatColor: "#B22234", dangerColor: "#B22234",
    tacticalHighlight: "#FFFFFF", overlayAccent: "#B22234", pulseColor: "#3C3B6E",
    contrast: "#ffffff",
  },
  URU: {
    id: "URU", family: "calm", intensity: 0.70, shadowLanguage: "soft",
    auraColor: "#0038A8", heatColor: "#FCD116", dangerColor: "#0038A8",
    tacticalHighlight: "#FCD116", overlayAccent: "#FFFFFF", pulseColor: "#0038A8",
    contrast: "#ffffff",
  },
  CHI: {
    id: "CHI", family: "fierce", intensity: 0.90, shadowLanguage: "halo",
    auraColor: "#D52B1E", heatColor: "#D52B1E", dangerColor: "#D52B1E",
    tacticalHighlight: "#0033A0", overlayAccent: "#FFFFFF", pulseColor: "#D52B1E",
    contrast: "#ffffff",
  },
  ECU: {
    id: "ECU", family: "bright", intensity: 0.88, shadowLanguage: "halo",
    auraColor: "#FFD100", heatColor: "#EF3340", dangerColor: "#EF3340",
    tacticalHighlight: "#0072CE", overlayAccent: "#FFD100", pulseColor: "#EF3340",
    contrast: "#1a1500",
  },
  JPN: {
    id: "JPN", family: "minimal", intensity: 0.65, shadowLanguage: "sharp",
    auraColor: "#BC002D", heatColor: "#BC002D", dangerColor: "#BC002D",
    tacticalHighlight: "#FFFFFF", overlayAccent: "#BC002D", pulseColor: "#BC002D",
    contrast: "#ffffff",
  },
  CUSTOM: {
    id: "CUSTOM", family: "neon", intensity: 0.70, shadowLanguage: "diffuse",
    auraColor: "var(--team-primary)", heatColor: "var(--team-tertiary)",
    dangerColor: "var(--team-tertiary)", tacticalHighlight: "var(--team-secondary)",
    overlayAccent: "var(--team-primary)", pulseColor: "var(--team-primary)",
    contrast: "#ffffff",
  },
};

export function getIdentityProfile(id: TeamId): TeamIdentityProfile {
  return PROFILES[id] ?? fallbackProfile(id);
}

/** Map shadow language to a numeric spread (px) for the glow. */
function spreadForLanguage(lang: ShadowLanguage): number {
  switch (lang) {
    case "sharp": return 18;
    case "diffuse": return 28;
    case "soft": return 22;
    case "halo": return 40;
  }
}

/**
 * Emit emotional identity tokens to CSS variables on documentElement.
 * Called by `applyLiveTheme` AFTER the base palette tokens are written.
 * Safe to call on the server (no-ops without a `document`).
 */
export function applyIdentityTokens(id: TeamId, _basePalette?: TeamPalette): void {
  if (typeof document === "undefined") return;
  const r = document.documentElement.style;
  const p = getIdentityProfile(id);
  const spread = spreadForLanguage(p.shadowLanguage);
  const intensityPct = Math.round(p.intensity * 100);

  r.setProperty("--team-identity-family", p.family);
  r.setProperty("--team-identity-intensity", String(p.intensity));
  r.setProperty("--team-aura-color", p.auraColor);
  r.setProperty("--team-heat-color", p.heatColor);
  r.setProperty("--team-danger-color", p.dangerColor);
  r.setProperty("--team-tactical-highlight", p.tacticalHighlight);
  r.setProperty("--team-overlay-accent", p.overlayAccent);
  r.setProperty("--team-pulse-color", p.pulseColor);
  r.setProperty("--team-contrast", p.contrast);

  // Derived shadow language — drives `.shadow-team-identity`, hero rings,
  // featured fan frames, prediction glow, momentum bars.
  r.setProperty(
    "--team-shadow",
    `0 0 ${spread}px color-mix(in oklab, ${p.auraColor} ${Math.min(intensityPct, 80)}%, transparent)`,
  );
  r.setProperty(
    "--team-shadow-strong",
    `0 0 ${spread + 18}px color-mix(in oklab, ${p.auraColor} ${Math.min(intensityPct + 10, 95)}%, transparent)`,
  );
  r.setProperty(
    "--team-aura",
    `radial-gradient(60% 80% at 50% 0%, color-mix(in oklab, ${p.auraColor} ${Math.round(p.intensity * 28)}%, transparent), transparent 70%)`,
  );
  r.setProperty(
    "--team-stadium-aura",
    `radial-gradient(120% 90% at 50% 100%, color-mix(in oklab, ${p.auraColor} ${Math.round(p.intensity * 18)}%, transparent), transparent 75%)`,
  );
  r.setProperty(
    "--team-heat",
    `radial-gradient(60% 60% at 50% 50%, color-mix(in oklab, ${p.heatColor} 55%, transparent), transparent 80%)`,
  );
  r.setProperty(
    "--team-frame-ring",
    `0 0 0 1px color-mix(in oklab, ${p.overlayAccent} 50%, transparent), 0 0 ${spread}px color-mix(in oklab, ${p.auraColor} 30%, transparent)`,
  );

  // Reflect family + shadow-language as data attributes so CSS can
  // tune animation tempo per emotional family without touching JS.
  document.documentElement.setAttribute("data-team-family", p.family);
  document.documentElement.setAttribute("data-team-shadow", p.shadowLanguage);
}

export const IDENTITY_PROFILES = PROFILES;